import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuth } from '@/stores/authStore'
import { useEmailVerificationModal } from '@/stores/emailVerificationStore'
import { useRecruiterSubscriptionModal } from '@/stores/recruiterSubscriptionStore'
import type { CandidateProfileStatsResponse } from '@/types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add auth token and language
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuth.getState()
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    // Add Accept-Language header from localStorage
    const language = localStorage.getItem('language') || 'en'
    if (config.headers) {
      config.headers['Accept-Language'] = language
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - Handle token refresh and email verification
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: string; message?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Check for email verification required (403 error)
    if (
      error.response?.status === 403 &&
      error.response.data?.error === 'Email verification required'
    ) {
      // Show email verification modal
      useEmailVerificationModal.getState().showModal()
      return Promise.reject(error)
    }

    // Check for pack required (403 error) — recruiter has no paid pack
    if (
      error.response?.status === 403 &&
      error.response.data?.error === 'No active pack found. Please purchase a pack to access candidate profiles.'
    ) {
      useRecruiterSubscriptionModal.getState().showModal()
      return Promise.reject(error)
    }

    // If 401 and we haven't retried yet, try to refresh token
    // Skip refresh logic for auth endpoints — those 401s mean wrong credentials
    const isAuthEndpoint = originalRequest.url?.includes('/auth/')
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      try {
        const { refreshToken } = useAuth.getState()
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refreshToken,
        })

        const { accessToken } = response.data
        useAuth.getState().updateAccessToken(accessToken)

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }

        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed, logout user
        useAuth.getState().logout()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// User Account Management
export const deleteUserAccount = async () => {
  return api.delete('/users/me/data')
}

// Pricing API
export const getCreditCosts = async () => {
  const response = await api.get('/pricing/credit-costs')
  return response.data
}

// Candidate Profile Stats API
export const getCandidateProfileStats = async (): Promise<CandidateProfileStatsResponse> => {
  const response = await api.get('/candidates/profile/stats')
  return response.data
}

// Candidate Profile API (employment status, relocation preference, etc.)
export const getCandidateProfile = async (): Promise<import('@/types').Candidate> => {
  const response = await api.get('/candidate/profile')
  return response.data
}

export default api
